import CartAnalytics from './CartAnalytics.js'
import CartConfig from './CartConfig.js'

/**
 * @class CartEventWatcher
 * @description Monitors cart events and updates through XHR and Fetch API interception
 */
class CartEventWatcher {
  static CART_STATE_SELECTOR = '[data-cart-state]'

  /**
   * @constructor
   * @param {CartConfig} config - Cart configuration instance
   * @param {CartAnalytics} analytics - Analytics instance
   */
  constructor(analytics = null) {
    this.config = new CartConfig()
    this.analytics = analytics
    this.cartState = this.getInitialCartState()
    this.initialize()
  }

  /**
   * @private
   * @returns {Object|null} Initial cart state from DOM
   */
  getInitialCartState() {
    const cartState = document.querySelector(CartEventWatcher.CART_STATE_SELECTOR)
    return cartState ? JSON.parse(cartState.textContent) : null
  }

  /**
   * @private
   * Initializes XHR and Fetch interceptors
   */
  initialize() {
    this.interceptXHR()
    this.interceptFetch()
    this.pushLoadedEvent()
  }

  /**
   * @private
   * Sends initial cart loaded event
   */
  pushLoadedEvent() {
    this.analytics?.initialCart(this.cartState)
  }

  /**
   * @private
   * @description Intercepts XMLHttpRequest calls to monitor cart-related endpoints.
   * Creates a proxy around the native XMLHttpRequest to capture and handle cart updates.
   * Only processes requests to cart endpoints (add/change/update).
   * Maintains original XHR functionality for non-cart endpoints.
   */
  interceptXHR() {
    const OriginalXHR = window.XMLHttpRequest
    const self = this

    window.XMLHttpRequest = function () {
      const xhr = new OriginalXHR()
      const originalOpen = xhr.open
      const originalSend = xhr.send

      xhr.open = function () {
        this._url = arguments[1]
        return originalOpen.apply(this, arguments)
      }

      xhr.send = function () {
        if (self.config.isCartEndpoint(this._url)) {
          this.addEventListener('load', function () {
            if (this.status === 200) {
              const response = JSON.parse(this.responseText)
              self.handleCartUpdate(response, this._url)
            }
          })
        }
        return originalSend.apply(this, arguments)
      }

      return xhr
    }
  }

  /**
   * @private
   * @description Intercepts Fetch API calls to monitor cart-related endpoints.
   * Creates a proxy around the native fetch to capture and handle cart updates.
   * Only processes requests to cart endpoints (add/change/update).
   * Maintains original fetch functionality for non-cart endpoints.
   * Clones responses to allow multiple reads of the response body.
   */
  interceptFetch() {
    const originalFetch = window.fetch
    const self = this

    window.fetch = function (url, options) {
      if (typeof url === 'string' && self.config.isCartEndpoint(url)) {
        return originalFetch.apply(this, arguments).then(async (response) => {
          if (response.ok) {
            const clone = response.clone()
            const responseData = await clone.json()
            self.handleCartUpdate(responseData, url)
          }
          return response
        })
      }
      return originalFetch.apply(this, arguments)
    }
  }

  /**
   * @private
   * @param {Object} responseData - The response data from the cart endpoint
   * @param {string} url - The URL of the cart endpoint that was called
   * @description Processes cart updates from intercepted requests.
   * Handles different endpoint types (add/change/update) appropriately.
   * Detects changes in cart state and triggers relevant analytics events.
   * For 'add' endpoints, updates single items.
   * For other endpoints, updates entire cart state.
   */
  handleCartUpdate(responseData, url) {
    const prevCartState = JSON.parse(JSON.stringify(this.cartState)) // Deep copy
    const endpointType = this.config.getEndpointType(url)

    // if (!prevCartState) {
    //   return
    // }

    // Update cart state based on endpoint type
    if (endpointType === 'add') {
      // For 'add' endpoint, response is a single item
      const newItem = responseData.items[0]
      this.updateCartStateForAdd(newItem)
    } else {
      // For other endpoints, response is the full cart
      this.cartState = responseData
    }

    const changes = this.detectCartChanges(prevCartState, this.cartState, endpointType, responseData)

    if (changes.added.length > 0) {
      if (this.analytics) {
        this.analytics.cartChange('add', changes.added)
      }
    }

    if (changes.removed.length > 0) {
      if (this.analytics) {
        this.analytics.cartChange('remove', changes.removed)
      }
    }
  }

  /**
   * @private
   * @param {Object} newItem - The new item to be added or updated in the cart
   * @description Updates cart state when a new item is added.
   * Creates a deep copy of current cart state to maintain immutability.
   * Initializes items array if it doesn't exist.
   * Updates existing items or adds new items as needed.
   * Preserves existing item properties while updating with new values.
   */
  updateCartStateForAdd(newItem) {
    // Create a deep copy of the current cart state
    const updatedCart = JSON.parse(JSON.stringify(this.cartState))

    // Initialize items array if it doesn't exist
    if (!updatedCart.items) {
      updatedCart.items = []
    }

    // Check if item already exists in cart
    const existingItemIndex = updatedCart.items.findIndex((item) => item.id === newItem.id)

    if (existingItemIndex !== -1) {
      // Update existing item
      updatedCart.items[existingItemIndex] = {
        ...updatedCart.items[existingItemIndex],
        ...newItem
      }
    } else {
      // Add new item
      updatedCart.items.push(newItem)
    }

    // Update cart state
    this.cartState = updatedCart
  }

  /**
   * @private
   * @param {Object} prevState - Previous cart state
   * @param {Object} newState - New cart state
   * @param {string} endpointType - Type of cart endpoint (add/change/update)
   * @param {Object} responseData - Raw response data from the cart endpoint
   * @returns {Object} changes - Object containing arrays of added and removed items
   * @description Detects changes between previous and new cart states.
   * Handles both single-item updates (add endpoint) and full cart updates.
   * Calculates quantity differences for existing items.
   * Tracks both additions and removals.
   * Returns changes in format: { added: [], removed: [] }
   */
  detectCartChanges(prevState, newState, endpointType, responseData) {
    const changes = {
      added: [],
      removed: []
    }

    if (endpointType === 'add') {
      // For 'add' endpoint, we only need to check the single item response
      const newItem = Array.isArray(responseData.items) && responseData.items.length > 0 ? responseData.items[0] : {}
      const prevItem = prevState.items?.find((item) => item.id === newItem.id)
      const prevQuantity = prevItem?.quantity || 0

      // If Prev state was empty, all items are added
      if (Object.keys(prevState).length === 0) {
        changes.added.push({
          ...newItem,
          quantity: newItem.quantity
        })
        return changes
      }

      if (newItem.quantity > prevQuantity) {
        changes.added.push({
          ...newItem,
          quantity: newItem.quantity - prevQuantity
        })
      } else if (newItem.quantity < prevQuantity) {
        changes.removed.push({
          ...newItem,
          quantity: prevQuantity - newItem.quantity
        })
      }
    } else {
      // For other endpoints, compare full cart states
      const prevItems = new Map(prevState.items?.map((item) => [item.id, item]) || [])
      const newItems = new Map(newState.items?.map((item) => [item.id, item]) || [])

      // Check for additions and quantity increases
      newItems.forEach((item, id) => {
        const prevItem = prevItems.get(id)
        const prevQuantity = prevItem?.quantity || 0

        if (item.quantity > prevQuantity) {
          changes.added.push({
            ...item,
            quantity: item.quantity - prevQuantity
          })
        }
      })

      // Check for removals and quantity decreases
      prevItems.forEach((item, id) => {
        const newItem = newItems.get(id)
        const newQuantity = newItem?.quantity || 0

        if (item.quantity > newQuantity) {
          changes.removed.push({
            ...item,
            quantity: item.quantity - newQuantity
          })
        }
      })
    }

    return changes
  }
}

export default CartEventWatcher
